import { isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { Auth } from '@freelancer/auth';
import { createEffect } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  distinctUntilChanged,
  filter,
  map,
  pairwise,
  tap,
} from 'rxjs/operators';
import { DATASTORE_CONFIG } from '../datastore.config';
import type { DatastoreConfig } from '../datastore.interface';
import { LocalCacheStorage } from '../local-cache/local-cache-storage.service';
import type { StoreState } from '../store.model';

/**
 * Clears both the in-memory store and the IndexedDB local cache
 * when the authenticated user changes.
 *
 * Gated by the `clearStoreOnAuthChange` config flag
 *
 * Triggers on:
 *   - Login:        undefined  -> userId "X"
 *   - Logout:       userId "X" -> undefined
 */
@Injectable()
export class DatastoreClearEffect {
  private auth = inject(Auth);
  private store$ = inject<Store<StoreState>>(Store);
  private localCacheStorage = inject(LocalCacheStorage);
  private platformId = inject(PLATFORM_ID);
  private datastoreConfig = inject<DatastoreConfig>(DATASTORE_CONFIG);

  readonly clearOnAuthChange$ = createEffect(
    () =>
      this.auth.authState$.pipe(
        filter(
          () =>
            !!this.datastoreConfig.clearStoreOnAuthChange &&
            isPlatformBrowser(this.platformId),
        ),
        map(state => state?.userId),
        distinctUntilChanged(),
        pairwise(),
        tap(() => {
          this.store$.dispatch({ type: 'CLEAR_STORE', payload: {} });
          this.localCacheStorage.clear();
        }),
      ),
    { dispatch: false },
  );
}
